import React from 'react';
import { cn } from '@/lib/utils';

interface MobileNumberInputProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'onChange' | 'value' | 'type' | 'prefix'
    > {
    value: string;
    onValueChange: (value: string) => void;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    align?: 'left' | 'right';
    sizeVariant?: 'default' | 'large';
    error?: boolean;
}

const DECIMAL_RE = /^\d*(?:\.\d{0,2})?$/;

export const MobileNumberInput = React.forwardRef<HTMLInputElement, MobileNumberInputProps>(
    (
        {
            value,
            onValueChange,
            prefix,
            suffix,
            align = 'right',
            sizeVariant = 'default',
            className,
            error,
            onBlur,
            ...props
        },
        ref
    ) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const next = e.target.value;
            if (next === '' || DECIMAL_RE.test(next)) {
                onValueChange(next);
            }
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (value !== '' && !Number.isNaN(Number(value))) {
                const normalized = Number(value).toFixed(2);
                if (normalized !== value) onValueChange(normalized);
            }
            onBlur?.(e);
        };

        const sizeClasses =
            sizeVariant === 'large'
                ? 'h-14 text-2xl font-semibold'
                : 'h-12 text-base';

        return (
            <div
                className={cn(
                    'flex items-center w-full rounded-xl border bg-background overflow-hidden',
                    'focus-within:ring-2 focus-within:ring-ring',
                    error ? 'border-destructive focus-within:ring-destructive' : 'border-input',
                    sizeClasses,
                    className
                )}
            >
                {prefix && (
                    <span className="pl-4 pr-2 text-muted-foreground flex-shrink-0">
                        {prefix}
                    </span>
                )}
                <input
                    ref={ref}
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    autoCorrect="off"
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={cn(
                        'flex-1 bg-transparent border-0 outline-none min-w-0',
                        'px-2 placeholder:text-muted-foreground',
                        align === 'right' ? 'text-right' : 'text-left',
                        !prefix && 'pl-4',
                        !suffix && 'pr-4'
                    )}
                    {...props}
                />
                {suffix && (
                    <span className="pl-2 pr-4 text-muted-foreground flex-shrink-0">
                        {suffix}
                    </span>
                )}
            </div>
        );
    }
);

MobileNumberInput.displayName = 'MobileNumberInput';
